// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/MemberSpecific.js
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faExclamationTriangle, faArrowLeft, faHeart, faMoneyBill } from '@fortawesome/free-solid-svg-icons';
import { ProgressSpinner } from 'primereact/progressspinner';
import PackageCards from '../components/MemberSpecific/PackageCards';
import { formatKey, formatValue, getIcon, ALLOWED_FIELDS } from '../components/MemberSpecific/MemberSpecificUtils';
import '../css/MemberSpecific.scss';
import { API_URLS, getSelectedLanguage } from '../api/api';
import { languageManager } from '../utils/loadLanguage';

const MemberSpecific = ({ userId, onCancel }) => {
  const [specificData, setSpecificData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [translations, setTranslations] = useState({});

  // Load translations
  useEffect(() => {
    async function loadLang() {
      const lang = getSelectedLanguage();
      await languageManager.loadLanguage(lang);
      setTranslations({
        ...(languageManager.translations.memberSpecificPage || {}),
        ...(languageManager.translations.specificDataKeys || {}),
        ...(languageManager.translations.common || {})
      });
    }
    loadLang();
  }, []);

  // Fetch specific data
  useEffect(() => {
    if (!userId) {
      setError(translations.invalidUserId || 'Invalid or missing User ID');
      setIsLoading(false);
      return;
    }

    const fetchSpecificData = async () => {
      setIsLoading(true);
      setError(null);
      setSpecificData(null);

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error(translations.noToken || 'No authentication token found. Please log in.');
        }

        const apiUrl = API_URLS.SPECIFIC_USER(userId);
        const response = await fetch(apiUrl, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          let errorData = { message: `${translations.fetchError || 'Failed to fetch data'}: ${response.status}` };
          try { errorData = await response.json(); } catch (e) { errorData.message = response.statusText || errorData.message; }
          throw new Error(errorData.message || translations.noProfileData || 'Unable to fetch specific profile data');
        }

        const data = await response.json();

        if (!Array.isArray(data) || data.length === 0) {
          setSpecificData(null);
        } else {
          const firstItem = data[0];
          if (!firstItem.profile_data || !firstItem.user_details || !firstItem.user_packages) {
            throw new Error(translations.incompleteData || 'Incomplete data structure from API');
          }
          setSpecificData(firstItem);
        }
      } catch (err) {
        setError(err.message || translations.fetchError || 'An error occurred while fetching data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSpecificData();
  }, [userId, translations]);

  // Render details list with icons
  const renderDetailsList = (dataObject) => {
    if (!dataObject || Object.keys(dataObject).length === 0) {
      return <p className="text-muted">{translations.noDetails || 'No details available'}</p>;
    }
    return (
      <ul className="details-list">
        {Object.entries(dataObject).map(([key, value]) => (
          <li key={key} className="details-item">
            <strong>
              {getIcon(key) && <FontAwesomeIcon icon={getIcon(key)} className="me-2" />}
              {formatKey(key, translations)}:
            </strong>
            <span>{formatValue(value, key, translations, getSelectedLanguage)}</span>
          </li>
        ))}
      </ul>
    );
  };

  if (isLoading) {
    return (
      <div className="member-specific-container">
        <ProgressSpinner className="loading-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="member-specific-container">
        <div className="error-card">
          <FontAwesomeIcon icon={faExclamationTriangle} className="error-icon" />
          <h4>{translations.error || 'Error!'}</h4>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!specificData) {
    return (
      <div className="member-specific-container">
        <div className="no-data-card">
          <FontAwesomeIcon icon={faUser} className="no-data-icon" />
          <h4>{translations.noData || 'No Data Found'}</h4>
          <p>{translations.noProfileDataForUser || 'No specific profile data found for this member'} (User ID: {userId})</p>
        </div>
      </div>
    );
  }

  const { profile_data, user_details, user_packages } = specificData;
  const profile = (profile_data && Array.isArray(profile_data) && profile_data.length > 0) ? profile_data[0] : {};
  const details = (user_details && Array.isArray(user_details) && user_details.length > 0) ? user_details[0] : {};
  const packages = Array.isArray(user_packages) ? user_packages : [];

  // Filter fields to display using single ALLOWED_FIELDS
  const personalInfo = {};
  const preferences = {};
  const assetsIncome = {};

  // Process profile fields
  Object.entries(ALLOWED_FIELDS.profile).forEach(([destination, fields]) => {
    fields.forEach((field) => {
      if (profile[field.key] !== undefined) {
        if (destination === 'personalInfo') {
          personalInfo[field.key] = profile[field.key];
        } else if (destination === 'assetsIncome') {
          assetsIncome[field.key] = profile[field.key];
        }
      }
    });
  });

  // Process details fields
  Object.entries(ALLOWED_FIELDS.details).forEach(([destination, fields]) => {
    fields.forEach((field) => {
      if (details[field.key] !== undefined) {
        if (destination === 'personalInfo') {
          personalInfo[field.key] = details[field.key];
        } else if (destination === 'preferences') {
          preferences[field.key] = details[field.key];
        } else if (destination === 'assetsIncome') {
          assetsIncome[field.key] = details[field.key];
        }
      }
    });
  });

  return (
    <div className="member-specific-container">
      <div className="header">
        <h2>{translations.profileTitle || 'Profile Details for'}: {profile.name || `User ID ${userId}`}</h2>
      </div>
      <div className="cards-grid">
        {/* Personal Info Card */}
        <div className="card personal-info">
          <div className="card-header">
            <FontAwesomeIcon icon={faUser} className="me-2" />
            {translations.personalInfo || 'Personal Information'}
          </div>
          <div className="card-body">
            {renderDetailsList(personalInfo)}
          </div>
        </div>

        {/* Preferences Card */}
        <div className="card preferences">
          <div className="card-header">
            <FontAwesomeIcon icon={faHeart} className="me-2" />
            {translations.preferences || 'Preferences & Matching'}
          </div>
          <div className="card-body">
            {renderDetailsList(preferences)}
          </div>
        </div>

        {/* Assets & Income Card */}
        <div className="card assets-income">
          <div className="card-header">
            <FontAwesomeIcon icon={faMoneyBill} className="me-2" />
            {translations.assetsAndIncome || 'Assets & Income'}
          </div>
          <div className="card-body">
            {renderDetailsList(assetsIncome)}
          </div>
        </div>

        {/* Profile Image Card */}
        <div className="card profile-image">
          <div className="card-header">
            <FontAwesomeIcon icon={faUser} className="me-2" />
            {translations.profileImage || 'Profile Image'}
          </div>
          <div className="card-body">
            {profile.profile_image ? (
              <img
                src={profile.profile_image}
                alt={translations.profileAlt ? `${translations.profileAlt} ${profile.name || 'member'}` : `Profile of ${profile.name || 'member'}`}
                className="profile-img"
              />
            ) : (
              <p className="text-muted">{translations.noImage || 'No image available'}</p>
            )}
          </div>
        </div>

        {/* Packages Cards */}
        <PackageCards
          packages={packages}
          allowedFields={ALLOWED_FIELDS.packages.packages}
          translations={translations}
          renderDetailsList={renderDetailsList}
          userId={userId}
        />
      </div>
    </div>
  );
};

export default MemberSpecific;